/**
 * =============================================================================
 * CREATE AGENT PAGE - ERC-8004 Agent Registration with added bsc support
 * =============================================================================
 * 
 * Multi-step form to create and register a new ERC-8004 agent
 * Matches the style of the 8004 Agents Explorer
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Activity, ChevronRight, ChevronLeft, Check, Wallet,
  Globe, Image, FileText, Settings, Database, Eye,
  Plus, X, AlertCircle, CheckCircle, Loader2, ExternalLink,
  Server, Link2, Shield, Cpu, CreditCard, Tag, Mail
} from 'lucide-react';

// =============================================================================
// CONFIGURATION
// =============================================================================

const SITE_NAME = '8004 Agents Explorer';

const CHAINS = {
  ethereum: {
    name: 'Ethereum',
    chainId: 1,
    wssUrl: process.env.ETHEREUM_WSS_URL,
    httpsUrl: process.env.ETHEREUM_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
      validation: '0x...'
    },
    explorer: 'https://etherscan.io',
    color: '#627eea',
    isMainnet: true  // Optional: add this to filter laterr
  },
  base: {
    name: 'Base',
    chainId: 8453,
    wssUrl: process.env.BASE_WSS_URL,
    httpsUrl: process.env.BASE_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
      //validation: '0x...'
    },
    explorer: 'https://basescan.org',
    color: '#0052ff',
    isMainnet: true
  },
  polygon: {
    name: 'Polygon',
    chainId: 137,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432', // Your Polygon mainnet contract address
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
      validation: '0x...'
    },
    explorer: 'https://polygonscan.com',
    color: '#8247e5',
    isMainnet: true
  },
  arbitrum: {
    name: 'Arbitrum',
    chainId: 42161,
    wssUrl: process.env.ARBITRUM_WSS_URL,
    httpsUrl: process.env.ARBITRUM_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
    },
    explorer: 'https://arbiscan.io',
    color: '#28A0F0',
    isMainnet: true
  },
  avalanche: {
  name: 'Avalanche',
  chainId: 43114,
  wssUrl: process.env.AVALANCHE_WSS_URL,
  httpsUrl: process.env.AVALANCHE_HTTPS_URL,
  contracts: {
    identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
    reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
  },
  explorer: 'https://snowtrace.io',
  color: '#E84142',
  isMainnet: true
},
  bsc: {
    name: 'BNB Chain',
    chainId: 56,
    wssUrl: process.env.BSC_WSS_URL,
    httpsUrl: process.env.BSC_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
      //validation: '0xCd40E749C64761DA2298436Fe0eA4dc23f58c1f3'
    },
    explorer: 'https://testnet.bscscan.com',
    color: '#F0B90B',
    isMainnet: true
  },
  monad: {
    name: 'Monad',
    chainId: 10143, // Monad mainnet - verify this when mainnet launches
    wssUrl: process.env.MONAD_WSS_URL,
    httpsUrl: process.env.MONAD_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
    },
    explorer: 'https://explorer.monad.xyz',
    color: '#836EF9',
    isMainnet: true
  },
  gnosis: {
    name: 'Gnosis',
    chainId: 100,
    wssUrl: process.env.GNOSIS_WSS_URL,
    httpsUrl: process.env.GNOSIS_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
    },
    explorer: 'https://gnosisscan.io',
    color: '#04795B',
    isMainnet: true
  },
  scroll: {
    name: 'Scroll',
    chainId: 534352,
    wssUrl: process.env.SCROLL_WSS_URL,
    httpsUrl: process.env.SCROLL_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
    },
    explorer: 'https://scrollscan.com',
    color: '#FFEEDA',
    isMainnet: true
  },
  celo: {
    name: 'Celo',
    chainId: 42220,
    wssUrl: process.env.CELO_WSS_URL,
    httpsUrl: process.env.CELO_HTTPS_URL,
    contracts: {
      identity: '0x8004A169FB4a3325136EB29fA0ceB6D2e539a432',
      reputation: '0x8004BAa17C55a88189AE136b182e5fdA19dE9b63',
    },
    explorer: 'https://celoscan.io',
    color: '#FCFF52',
    isMainnet: true,
  },
  bscTestnet: {
    name: 'BSC Testnet',
    chainId: 97,
    wssUrl: process.env.BSC_TESTNET_WSS_URL,
    httpsUrl: process.env.BSC_TESTNET_HTTPS_URL,
    contracts: {
      identity: '0x8004A818BFB912233c491871b3d84c89A494BD9e',
      reputation: '0x8004B663056A597Dffe9eCcC1965A193B7388713',
      //validation: '0xCd40E749C64761DA2298436Fe0eA4dc23f58c1f3'
    },
    explorer: 'https://testnet.bscscan.com',
    color: '#F0B90B'
  },
  sepolia: {
    name: 'Sepolia',
    chainId: 11155111,
    contracts: {
      identity: '0x8004A818BFB912233c491871b3d84c89A494BD9e',
      reputation: '0x8004B663056A597Dffe9eCcC1965A193B7388713',
      validation: '0x8004CB39f29c09145F24Ad9dDe2A108C1A2cdfC5'
    },
    explorer: 'https://sepolia.etherscan.io',
    color: '#627eea'
  },
  baseSepolia: {
    name: 'Base Sepolia',
    chainId: 84532,
    wssUrl: process.env.BASE_SEPOLIA_WSS_URL,
    httpsUrl: process.env.BASE_SEPOLIA_HTTPS_URL,
    contracts: {
      identity: '0x8004AA63c570c570eBF15376c0dB199918BFe9Fb',
      reputation: '0x8004bd8daB57f14Ed299135749a5CB5c42d341BF',
      //validation: '0x8004C269D0A5647E51E121FeB226200ECE932d55'
    },
    explorer: 'https://sepolia.basescan.org',
    color: '#0052ff'
  },
  polygonAmoy: {
    name: 'Polygon Amoy',
    chainId: 80002,
    contracts: {
      identity: '0x8004ad19E14B9e0654f73353e8a0B600D46C2898',
      reputation: '0x8004B12F4C2B42d00c46479e859C92e39044C930',
      validation: '0x8004C11C213ff7BaD36489bcBDF947ba5eee289B'
    },
    explorer: 'https://amoy.polygonscan.com',
    color: '#8247e5'
  },
  /*
  baseSepolia: {
    name: 'Base Sepolia',
    chainId: 84532,
    contracts: {
      identity: '0x8004AA63c570c570eBF15376c0dB199918BFe9Fb',
      reputation: '0x8004bd8daB57f14Ed299135749a5CB5c42d341BF',
      validation: '0x8004C269D0A5647E51E121FeB226200ECE932d55'
    },
    explorer: 'https://sepolia.basescan.org',
    color: '#0052ff'
  },
  bscTestnet: {
    name: 'BSC Testnet',
    chainId: 97,
    wssUrl: process.env.BSC_TESTNET_WSS_URL,
    httpsUrl: process.env.BSC_TESTNET_HTTPS_URL,
    contracts: {
      identity: '0x4f8c8694eAB93bbF7616EDD522503544E61E7dB7',
      reputation: '0xe55d10F699bCF2207573b7Be697C983C0d92c2b5',
      validation: '0xCd40E749C64761DA2298436Fe0eA4dc23f58c1f3'
    },
    explorer: 'https://testnet.bscscan.com',
    color: '#F0B90B'
  },
  lineaSepolia: {
    name: 'Linea Sepolia',
    chainId: 59141,
    contracts: {
      identity: '0x8004aa7C931bCE1233973a0C6A667f73F66282e7',
      reputation: '0x8004bd8483b99310df121c46ED8858616b2Bba02',
      validation: '0x8004c44d1EFdd699B2A26e781eF7F77c56A9a4EB'
    },
    explorer: 'https://sepolia.lineascan.build',
    color: '#121212'
  },
  hyperevmTestnet: {
    name: 'HyperEVM Testnet',
    chainId: 998,
    contracts: {
      identity: '0x8004A9560C0edce880cbD24Ba19646470851C986',
      reputation: '0x8004b490779A65D3290a31fD96471122050dF671',
      validation: '0x8004C86198fdB8d8169c0405D510EC86cc7B0551'
    },
    explorer: 'https://explorer.hyperliquid-testnet.xyz',
    color: '#00d4aa'
  },
  skaleBaseSepolia: {
    name: 'SKALE Base Sepolia',
    chainId: 1351057110,
    wssUrl: process.env.SKALE_BASE_SEPOLIA_WSS_URL,
    httpsUrl: process.env.SKALE_BASE_SEPOLIA_HTTPS_URL,
    contracts: {
      identity: '0x4fa7900596c9830664406d3796952c59ec4133d9',
      reputation: '0x9b9d23a47697691ef1016906d1f8ddfc009e6a69',
      validation: '0x34ae1196b1609e01ebc90b75c802b2ea87203f13'
    },
    explorer: 'https://giant-half-dual-testnet.explorer.testnet.skalenodes.com',
    color: '#00c08b'
  }
  */
};

// Common OASF Skills
const COMMON_SKILLS = [
  'natural_language_processing/summarization',
  'natural_language_processing/translation',
  'natural_language_processing/sentiment_analysis',
  'code_generation/python',
  'code_generation/javascript',
  'data_analysis/visualization',
  'image_processing/generation',
  'image_processing/editing',
  'conversational_ai/chatbot',
  'task_automation/scheduling'
];

// Common OASF Domains
const COMMON_DOMAINS = [
  'finance_and_business/investment_services',
  'finance_and_business/accounting',
  'healthcare/diagnosis_support',
  'education/tutoring',
  'technology/software_development',
  'creative/content_creation',
  'research/data_analysis',
  'customer_service/support'
];

// =============================================================================
// IDENTITY CONTRACT ABI (EIP-8004 Standard)
// =============================================================================

const IDENTITY_ABI = [
  // Registration function with tokenURI only (simplest)
  {
    "inputs": [
      { "internalType": "string", "name": "tokenURI", "type": "string" }
    ],
    "name": "register",
    "outputs": [
      { "internalType": "uint256", "name": "agentId", "type": "uint256" }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  // Registration function with tokenURI and metadata entries
  {
    "inputs": [
      { "internalType": "string", "name": "tokenURI", "type": "string" },
      {
        "components": [
          { "internalType": "string", "name": "key", "type": "string" },
          { "internalType": "bytes", "name": "value", "type": "bytes" }
        ],
        "internalType": "struct IAgentRegistry.MetadataEntry[]",
        "name": "metadata",
        "type": "tuple[]"
      }
    ],
    "name": "register",
    "outputs": [
      { "internalType": "uint256", "name": "agentId", "type": "uint256" }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  // Registration function with no params (tokenURI added later)
  {
    "inputs": [],
    "name": "register",
    "outputs": [
      { "internalType": "uint256", "name": "agentId", "type": "uint256" }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  // Set metadata after registration
  {
    "inputs": [
      { "internalType": "uint256", "name": "agentId", "type": "uint256" },
      { "internalType": "string", "name": "key", "type": "string" },
      { "internalType": "bytes", "name": "value", "type": "bytes" }
    ],
    "name": "setMetadata",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  // Get metadata
  {
    "inputs": [
      { "internalType": "uint256", "name": "agentId", "type": "uint256" },
      { "internalType": "string", "name": "key", "type": "string" }
    ],
    "name": "getMetadata",
    "outputs": [
      { "internalType": "bytes", "name": "value", "type": "bytes" }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  // ERC-721 standard functions
  {
    "inputs": [
      { "internalType": "uint256", "name": "tokenId", "type": "uint256" }
    ],
    "name": "ownerOf",
    "outputs": [
      { "internalType": "address", "name": "", "type": "address" }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      { "internalType": "uint256", "name": "tokenId", "type": "uint256" }
    ],
    "name": "tokenURI",
    "outputs": [
      { "internalType": "string", "name": "", "type": "string" }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  // Events
  {
    "anonymous": false,
    "inputs": [
      { "indexed": true, "internalType": "uint256", "name": "agentId", "type": "uint256" },
      { "indexed": false, "internalType": "string", "name": "tokenURI", "type": "string" },
      { "indexed": true, "internalType": "address", "name": "owner", "type": "address" }
    ],
    "name": "Registered",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      { "indexed": true, "internalType": "uint256", "name": "agentId", "type": "uint256" },
      { "indexed": true, "internalType": "string", "name": "indexedKey", "type": "string" },
      { "indexed": false, "internalType": "string", "name": "key", "type": "string" },
      { "indexed": false, "internalType": "bytes", "name": "value", "type": "bytes" }
    ],
    "name": "MetadataSet",
    "type": "event"
  }
];

// =============================================================================
// MAIN CREATE AGENT COMPONENT
// =============================================================================

function CreateAgentPage() {
  // Wallet state
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [selectedChain, setSelectedChain] = useState('sepolia');
  const [currentChainId, setCurrentChainId] = useState(null);

  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    description: '',
    imageUrl: '',

    // Endpoints
    endpoints: [],

    // Capabilities
    skills: [],
    domains: [],
    tags: [],

    // Advanced Options
    trustMechanisms: {
      reputation: false,
      cryptoEconomic: false,
      teeAttestation: false
    },
    isActive: true,
    supportsHttp402: false,

    // Storage
    storageMethod: 'auto', // 'auto', 'ipfs', 'https'
    ipfsUrl: '',
    httpsUrl: ''
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [newEndpoint, setNewEndpoint] = useState({
    type: 'mcp',
    url: '',
    version: '',
    // MCP specific
    mcpTools: '',
    mcpPrompts: '',
    mcpResources: '',
    // A2A specific
    a2aSkill: '',
    // OASF specific
    oasfSkill: '',
    oasfDomain: '',
    // ENS specific
    ensName: '',
    // DID specific
    didIdentifier: '',
    // Wallet specific
    walletAddress: '',
    // Custom specific
    customName: ''
  });
  const [endpointSubTab, setEndpointSubTab] = useState('mcp');
  const [newTag, setNewTag] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newDomain, setNewDomain] = useState('');

  const steps = [
    { id: 1, name: 'Basic Info', icon: FileText },
    { id: 2, name: 'Endpoints', icon: Globe },
    { id: 3, name: 'Capabilities', icon: Tag },
    { id: 4, name: 'Advanced', icon: Settings },
    { id: 5, name: 'Storage', icon: Database },
    { id: 6, name: 'Review', icon: Eye }
  ];

  // =============================================================================
  // WALLET FUNCTIONS
  // =============================================================================

  async function connectWallet() {
    if (typeof window.ethereum === 'undefined') {
      alert('Please install MetaMask to use this feature');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      setWalletConnected(true);

      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      const parsedChainId = parseInt(chainId, 16);
      setCurrentChainId(parsedChainId);

      // Sync dropdown to match wallet's current chain
      const matchingChain = Object.entries(CHAINS).find(
        ([_, c]) => c.chainId === parsedChainId
      );
      if (matchingChain) {
        setSelectedChain(matchingChain[0]);
      }

      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setWalletConnected(false);
          setWalletAddress('');
        } else {
          setWalletAddress(accounts[0]);
        }
      });

      // Listen for chain changes
      window.ethereum.on('chainChanged', (chainId) => {
        const parsed = parseInt(chainId, 16);
        setCurrentChainId(parsed);

        // Sync dropdown to match wallet's current chain
        const matchingChain = Object.entries(CHAINS).find(
          ([_, c]) => c.chainId === parsed
        );
        if (matchingChain) {
          setSelectedChain(matchingChain[0]);
        }
      });
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  }

  async function switchChain(chainKey) {
    const chain = CHAINS[chainKey];
    if (!chain) return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x' + chain.chainId.toString(16) }]
      });
      setSelectedChain(chainKey);
    } catch (error) {
      // Chain not added, try to add it
      if (error.code === 4902) {
        // Would need to add chain - for testnets this might not work
        console.error('Chain not found in wallet');
      }
      console.error('Failed to switch chain:', error);
    }
  }

  // =============================================================================
  // FORM HANDLERS
  // =============================================================================

  function updateFormData(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function addEndpoint() {
    const endpoint = { ...newEndpoint };

    // Validate based on type
    if (endpointSubTab === 'web' && !endpoint.url) return;
    if (endpointSubTab === 'email' && !endpoint.url) return;
    if (endpointSubTab === 'mcp' && !endpoint.url) return;
    if (endpointSubTab === 'a2a' && !endpoint.url) return;
    if (endpointSubTab === 'oasf' && !endpoint.url) return;
    if (endpointSubTab === 'ens' && !endpoint.ensName) return;
    if (endpointSubTab === 'did' && !endpoint.didIdentifier) return;
    if (endpointSubTab === 'wallet' && !endpoint.walletAddress) return;
    if (endpointSubTab === 'custom' && (!endpoint.customName || !endpoint.url)) return;

    endpoint.type = endpointSubTab;

    setFormData(prev => ({
      ...prev,
      endpoints: [...prev.endpoints, endpoint]
    }));

    // Reset new endpoint form
    setNewEndpoint({
      type: 'mcp',
      url: '',
      version: '',
      mcpTools: '',
      mcpPrompts: '',
      mcpResources: '',
      a2aSkill: '',
      oasfSkill: '',
      oasfDomain: '',
      ensName: '',
      didIdentifier: '',
      walletAddress: '',
      customName: ''
    });
  }

  function removeEndpoint(index) {
    setFormData(prev => ({
      ...prev,
      endpoints: prev.endpoints.filter((_, i) => i !== index)
    }));
  }

  function addTag() {
    if (!newTag.trim()) return;
    if (formData.tags.includes(newTag.trim())) return;
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, newTag.trim()]
    }));
    setNewTag('');
  }

  function removeTag(tag) {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  }

  function addSkill(skill) {
    if (formData.skills.includes(skill)) return;
    setFormData(prev => ({
      ...prev,
      skills: [...prev.skills, skill]
    }));
    setNewSkill('');
  }

  function removeSkill(skill) {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  }

  function addDomain(domain) {
    if (formData.domains.includes(domain)) return;
    setFormData(prev => ({
      ...prev,
      domains: [...prev.domains, domain]
    }));
    setNewDomain('');
  }

  function removeDomain(domain) {
    setFormData(prev => ({
      ...prev,
      domains: prev.domains.filter(d => d !== domain)
    }));
  }

  function updateTrustMechanism(mechanism, value) {
    setFormData(prev => ({
      ...prev,
      trustMechanisms: {
        ...prev.trustMechanisms,
        [mechanism]: value
      }
    }));
  }

  // =============================================================================
  // METADATA GENERATION
  // =============================================================================

  function generateMetadata() {
    const metadata = {
      type: "https://eips.ethereum.org/EIPS/eip-8004#registration-v1",
      name: formData.name,
      description: formData.description,
      image: formData.imageUrl,
      endpoints: [],
      active: formData.isActive,
      x402Support: formData.supportsHttp402
    };

    // Only add image if provided
    if (formData.imageUrl && formData.imageUrl.trim()) {
      metadata.image = formData.imageUrl;
    }

    // Add endpoints in spec format
    formData.endpoints.forEach((ep) => {
      const endpointObj = {
        name: ep.customName || ep.type, // Use custom name if provided, otherwise type
        endpoint: ep.url || ep.ensName || ep.didIdentifier || ep.walletAddress
      };

      // Add version if provided
      if (ep.version) {
        endpointObj.version = ep.version;
      }

      // Add MCP-specific fields
      if (ep.type === 'mcp') {
        if (ep.mcpTools) {
          endpointObj.capabilities = ep.mcpTools.split(',').map(t => t.trim()).filter(Boolean);
        }
      }

      metadata.endpoints.push(endpointObj);
    });

    // Add trust mechanisms
    const supportedTrust = [];
    if (formData.trustMechanisms.reputation) {
      supportedTrust.push('reputation');
    }
    if (formData.trustMechanisms.cryptoEconomic) {
      supportedTrust.push('crypto-economic');
    }
    if (formData.trustMechanisms.teeAttestation) {
      supportedTrust.push('tee-attestation');
    }
    if (supportedTrust.length > 0) {
      metadata.supportedTrust = supportedTrust;
    }

    // Add tags if provided (custom extension for discovery)
    if (formData.tags && formData.tags.length > 0) {
      metadata.tags = formData.tags;
    }

    // Add OASF skills/domains at top level for discovery
    if (formData.skills && formData.skills.length > 0) {
      metadata.oasf_skills = formData.skills;
    }
    if (formData.domains && formData.domains.length > 0) {
      metadata.oasf_domains = formData.domains;
    }

    // Clean up empty endpoints array
    if (metadata.endpoints.length === 0) {
      delete metadata.endpoints;
    }

    return metadata;
  }

  function generateTokenURI() {
    const metadata = generateMetadata();

    if (formData.storageMethod === 'auto') {
      // Base64 encode the metadata as data URI
      const jsonString = JSON.stringify(metadata);
      const base64 = btoa(unescape(encodeURIComponent(jsonString)));
      return `data:application/json;base64,${base64}`;
    } else if (formData.storageMethod === 'ipfs') {
      return formData.ipfsUrl;
    } else if (formData.storageMethod === 'https') {
      return formData.httpsUrl;
    }

    return '';
  }

  // =============================================================================
  // SUBMIT TRANSACTION
  // =============================================================================

  async function submitTransaction() {
    if (!walletConnected) {
      setSubmitError('Please connect your wallet first');
      return;
    }

    const chain = CHAINS[selectedChain];
    if (currentChainId !== chain.chainId) {
      setSubmitError(`Please switch to ${chain.name} network`);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const tokenURI = generateTokenURI();

      // Import ethers dynamically
      const { ethers } = await import('ethers');

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const contract = new ethers.Contract(
        chain.contracts.identity,
        IDENTITY_ABI,
        signer
      );

      // Use the register(string tokenURI) function
      // This is the simplest overload that takes just the tokenURI
      console.log('Registering agent with tokenURI:', tokenURI.slice(0, 100) + '...');

      const tx = await contract['register(string)'](tokenURI);

      setSubmitSuccess({
        message: 'Transaction submitted! Waiting for confirmation...',
        txHash: tx.hash
      });

      // Wait for confirmation
      const receipt = await tx.wait();

      // Try to get the agent ID from the Registered event
      let agentId = null;
      for (const log of receipt.logs) {
        try {
          const parsed = contract.interface.parseLog({
            topics: log.topics,
            data: log.data
          });
          if (parsed && parsed.name === 'Registered') {
            agentId = parsed.args.agentId.toString();
            break;
          }
        } catch (e) {
          // Not our event, continue
        }
      }

      setSubmitSuccess({
        message: 'Agent created successfully!',
        txHash: tx.hash,
        agentId: agentId,
        chain: selectedChain
      });

    } catch (error) {
      console.error('Transaction failed:', error);

      // Parse common errors
      let errorMessage = error.message || 'Transaction failed';
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = 'Transaction was rejected by user';
      } else if (error.code === 'INSUFFICIENT_FUNDS') {
        errorMessage = 'Insufficient funds for gas';
      } else if (errorMessage.includes('execution reverted')) {
        errorMessage = 'Transaction reverted - check contract requirements';
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // =============================================================================
  // VALIDATION
  // =============================================================================

  function validateStep(step) {
    switch (step) {
      case 1:
        return formData.name.trim().length > 0 &&
          formData.description.trim().length >= 50;

      case 2:
        return true; // Endpoints are optional
      case 3:
        return true; // Capabilities are optional
      case 4:
        return true; // Advanced options are optional
      case 5:
        if (formData.storageMethod === 'ipfs') {
          return formData.ipfsUrl.trim().length > 0;
        }
        if (formData.storageMethod === 'https') {
          return formData.httpsUrl.trim().length > 0;
        }
        return true;
      case 6:
        return validateStep(1) && validateStep(5);
      default:
        return true;
    }
  }

  function canProceed() {
    return validateStep(currentStep);
  }

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Helmet>
        <title>Create Agent | {SITE_NAME}</title>
        <meta name="description" content="Create and register a new ERC-8004 AI agent on the blockchain" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#3498db] to-[#2980b9] rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Create Agent</h1>
                <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">ERC-8004 Registration</p>
              </div>
            </Link>

            {/* Wallet Connection */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              {/* Chain Selector */}
              <select
                value={selectedChain}
                onChange={(e) => switchChain(e.target.value)}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white max-w-[100px] sm:max-w-none"
              >
                {Object.entries(CHAINS).map(([key, chain]) => (
                  <option key={key} value={key}>{chain.name}</option>
                ))}
              </select>

              {walletConnected ? (
                <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-green-50 border border-green-200 rounded-lg">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs sm:text-sm font-medium text-green-700">
                    {walletAddress.slice(0, 4)}...{walletAddress.slice(-3)}
                  </span>
                </div>
              ) : (
                <button
                  onClick={connectWallet}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition font-medium text-xs sm:text-sm"
                >
                  <Wallet className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Connect Wallet</span>
                  <span className="sm:hidden">Connect</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Mobile: Horizontal scroll */}
          <div className="flex items-center justify-between gap-1 sm:gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition flex-shrink-0 ${isActive
                      ? 'bg-[#3498db] text-white'
                      : isCompleted
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                    <span className="text-[10px] sm:text-sm font-medium hidden sm:inline">{step.name}</span>
                    <span className="text-[10px] font-medium sm:hidden">{step.id}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300 flex-shrink-0 hidden sm:block" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile step indicator */}
          <div className="sm:hidden mt-2 text-center">
            <span className="text-xs text-gray-500">{steps.find(s => s.id === currentStep)?.name}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Step Content */}
          <div className="p-6">
            {currentStep === 1 && (
              <Step1BasicInfo
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
            {currentStep === 2 && (
              <Step2Endpoints
                formData={formData}
                newEndpoint={newEndpoint}
                setNewEndpoint={setNewEndpoint}
                endpointSubTab={endpointSubTab}
                setEndpointSubTab={setEndpointSubTab}
                addEndpoint={addEndpoint}
                removeEndpoint={removeEndpoint}
              />
            )}
            {currentStep === 3 && (
              <Step3Capabilities
                formData={formData}
                newTag={newTag}
                setNewTag={setNewTag}
                addTag={addTag}
                removeTag={removeTag}
                newSkill={newSkill}
                setNewSkill={setNewSkill}
                addSkill={addSkill}
                removeSkill={removeSkill}
                newDomain={newDomain}
                setNewDomain={setNewDomain}
                addDomain={addDomain}
                removeDomain={removeDomain}
              />
            )}
            {currentStep === 4 && (
              <Step4Advanced
                formData={formData}
                updateFormData={updateFormData}
                updateTrustMechanism={updateTrustMechanism}
              />
            )}
            {currentStep === 5 && (
              <Step5Storage
                formData={formData}
                updateFormData={updateFormData}
              />
            )}
            {currentStep === 6 && (
              <Step6Review
                formData={formData}
                selectedChain={selectedChain}
                generateMetadata={generateMetadata}
                walletConnected={walletConnected}
                isSubmitting={isSubmitting}
                submitError={submitError}
                submitSuccess={submitSuccess}
                submitTransaction={submitTransaction}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < 6 ? (
              <button
                onClick={() => setCurrentStep(prev => Math.min(6, prev + 1))}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// =============================================================================
// STEP 1: BASIC INFO
// =============================================================================

function Step1BasicInfo({ formData, updateFormData }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Basic Information</h2>
        <p className="text-gray-600">
          The name, image, and description will be displayed across all ERC-721 compatible apps, explorers, and marketplaces.
        </p>
      </div>

      {/* Agent Name */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Agent Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value.slice(0, 100))}
          placeholder="My Awesome Agent"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db]"
          maxLength={100}
        />
        <p className="mt-1 text-xs text-gray-500">
          Make it descriptive, concise, and memorable ({formData.name.length}/100 characters)
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value.slice(0, 2000))}
          placeholder="Explain what your agent does, how it works, what problems it solves, pricing if applicable, and how users can interact with it..."
          rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db] resize-none"
          maxLength={2000}
        />
        <p className="mt-1 text-xs text-gray-500">
          {formData.description.length}/2000 - Minimum 50 characters recommended
          {formData.description.length < 50 && formData.description.length > 0 && (
            <span className="text-orange-500 ml-2">
              ({50 - formData.description.length} more characters needed)
            </span>
          )}
        </p>
      </div>

      {/* Image URL */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Image URL <span className="text-gray-400 text-xs font-normal">(optional)</span>
        </label>
        <input
          type="url"
          value={formData.imageUrl}
          onChange={(e) => updateFormData('imageUrl', e.target.value)}
          placeholder="https://example.com/agent-image.png or ipfs://..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db]"
        />
        <p className="mt-1 text-xs text-gray-500">
          High-quality image (PNG, SVG, or WebP recommended). Supports HTTP, HTTPS, or IPFS URLs.
        </p>

        {/* Image Preview */}
        {formData.imageUrl && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
            <img
              src={formData.imageUrl}
              alt="Agent preview"
              className="w-32 h-32 rounded-xl border border-gray-200 object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STEP 2: ENDPOINTS
// =============================================================================

function Step2Endpoints({
  formData,
  newEndpoint,
  setNewEndpoint,
  endpointSubTab,
  setEndpointSubTab,
  addEndpoint,
  removeEndpoint
}) {
  const endpointTypes = [
    { id: 'web', name: 'Web', icon: Globe },        // ADD
    { id: 'email', name: 'Email', icon: Mail },     // ADD (import Mail from lucide-react)
    { id: 'mcp', name: 'MCP', icon: Server },
    { id: 'a2a', name: 'A2A', icon: Link2 },
    { id: 'oasf', name: 'OASF', icon: Globe },
    { id: 'ens', name: 'ENS', icon: Globe },
    { id: 'did', name: 'DID', icon: Shield },
    { id: 'wallet', name: 'Wallet', icon: Wallet },
    { id: 'custom', name: 'Custom', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Communication Endpoints</h2>
        <p className="text-gray-600">
          Add at least one endpoint so others can communicate with your agent. Endpoints define how users and other agents can reach your agent.
        </p>
      </div>

      {/* Existing Endpoints */}
      {formData.endpoints.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Added Endpoints</h3>
          {formData.endpoints.map((ep, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-[#3498db] text-white text-xs font-medium rounded uppercase">
                  {ep.type}
                </span>
                <span className="text-sm text-gray-700 font-mono">
                  {ep.url || ep.ensName || ep.didIdentifier || ep.walletAddress}
                </span>
                {ep.version && (
                  <span className="text-xs text-gray-500">v{ep.version}</span>
                )}
              </div>
              <button
                onClick={() => removeEndpoint(index)}
                className="p-1 text-red-500 hover:bg-red-50 rounded transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Endpoint */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Add New Endpoint</h3>
        </div>

        {/* Endpoint Type Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {endpointTypes.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setEndpointSubTab(type.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition ${endpointSubTab === type.id
                  ? 'text-[#3498db] border-b-2 border-[#3498db] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {type.name}
              </button>
            );
          })}
        </div>

        {/* Endpoint Form */}
        <div className="p-4 space-y-4">

          {/* Web & Email Endpoints */}
          {endpointSubTab === 'web' && (
            <>
              <p className="text-sm text-gray-600">Public website or web interface for your agent</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website URL *</label>
                <input
                  type="url"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://web.myagent.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          {endpointSubTab === 'email' && (
            <>
              <p className="text-sm text-gray-600">Email address for contacting your agent</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="mail@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          {endpointSubTab === 'mcp' && (
            <>
              <p className="text-sm text-gray-600">Model Context Protocol endpoint for AI tools and prompts</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://mcp.example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={newEndpoint.version}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="1.0.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MCP Tools (optional)</label>
                  <input
                    type="text"
                    value={newEndpoint.mcpTools}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, mcpTools: e.target.value }))}
                    placeholder="tool1, tool2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
              </div>
            </>
          )}

          {endpointSubTab === 'a2a' && (
            <>
              <p className="text-sm text-gray-600">Agent-to-Agent communication endpoint</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://agent.example/.well-known/agent-card.json"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={newEndpoint.version}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="1.0.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">A2A Skill</label>
                  <input
                    type="text"
                    value={newEndpoint.a2aSkill}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, a2aSkill: e.target.value }))}
                    placeholder="skill_name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
              </div>
            </>
          )}

          {endpointSubTab === 'oasf' && (
            <>
              <p className="text-sm text-gray-600">Open Agentic Schema Framework endpoint</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="ipfs://{cid} or https://api.example.com/oasf"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input
                  type="text"
                  value={newEndpoint.version}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="0.8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <p className="text-xs text-gray-500">
                Add OASF skills and domains in the Capabilities tab (Step 3)
              </p>
            </>
          )}

          {endpointSubTab === 'ens' && (
            <>
              <p className="text-sm text-gray-600">Ethereum Name Service domain for your agent</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ENS Domain Name *</label>
                <input
                  type="text"
                  value={newEndpoint.ensName}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, ensName: e.target.value }))}
                  placeholder="myagent.eth"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input
                  type="text"
                  value={newEndpoint.version}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          {endpointSubTab === 'did' && (
            <>
              <p className="text-sm text-gray-600">Decentralized Identifier for your agent</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DID Identifier *</label>
                <input
                  type="text"
                  value={newEndpoint.didIdentifier}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, didIdentifier: e.target.value }))}
                  placeholder="did:method:foobar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input
                  type="text"
                  value={newEndpoint.version}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="v1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          {endpointSubTab === 'wallet' && (
            <>
              <p className="text-sm text-gray-600">Wallet address in CAIP-10 format</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wallet Address *</label>
                <input
                  type="text"
                  value={newEndpoint.walletAddress}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, walletAddress: e.target.value }))}
                  placeholder="eip155:1:0x..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          {endpointSubTab === 'custom' && (
            <>
              <p className="text-sm text-gray-600">Custom endpoint with your own naming</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint Name *</label>
                  <input
                    type="text"
                    value={newEndpoint.customName}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, customName: e.target.value }))}
                    placeholder="my_api"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={newEndpoint.version}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="1.0.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL *</label>
                <input
                  type="url"
                  value={newEndpoint.url}
                  onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://api.example.com/custom"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              </div>
            </>
          )}

          <button
            onClick={addEndpoint}
            className="flex items-center gap-2 px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Endpoint
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        You can skip adding endpoints now and add them later, but it's recommended to add at least one endpoint so others know how to communicate with your agent.
      </p>
    </div>
  );
}

// =============================================================================
// STEP 3: CAPABILITIES
// =============================================================================

function Step3Capabilities({
  formData,
  newTag, setNewTag, addTag, removeTag,
  newSkill, setNewSkill, addSkill, removeSkill,
  newDomain, setNewDomain, addDomain, removeDomain
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Capabilities (Optional)</h2>
        <p className="text-gray-600">
          Add OASF (Open Agentic Schema Framework) skills and domains to help users discover your agent
        </p>
        <p className="text-sm text-gray-500 mt-2">
          OASF provides a standardized taxonomy for agent capabilities.{' '}
          <a href="https://oasf.io" target="_blank" rel="noopener noreferrer" className="text-[#3498db] hover:underline">
            View the complete list
          </a>
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Tags</label>
        <p className="text-xs text-gray-500 mb-2">
          Free-form discovery keywords stored in your agent's metadata (e.g., "defi", "chatbot").
          Unlike OASF skills and domains, these don't need to follow any specific format.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addTag()}
            placeholder="Add a tag..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
          />
          <button
            onClick={addTag}
            className="px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.tags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                #{tag}
                <button onClick={() => removeTag(tag)} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* OASF Skills */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">OASF Skills (optional)</label>
        <p className="text-xs text-gray-500 mb-2">Use slash-separated paths (e.g., "natural_language_processing/summarization")</p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addSkill(newSkill)}
            placeholder="Add a skill..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
          />
          <button
            onClick={() => addSkill(newSkill)}
            className="px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Common Skills */}
        <p className="text-xs text-gray-500 mb-2">Common skills (click to add):</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {COMMON_SKILLS.filter(s => !formData.skills.includes(s)).slice(0, 6).map(skill => (
            <button
              key={skill}
              onClick={() => addSkill(skill)}
              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs transition"
            >
              + {skill}
            </button>
          ))}
        </div>

        {formData.skills.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.skills.map(skill => (
              <span key={skill} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                {skill}
                <button onClick={() => removeSkill(skill)} className="hover:text-purple-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* OASF Domains */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">OASF Domains (optional)</label>
        <p className="text-xs text-gray-500 mb-2">Use slash-separated paths (e.g., "finance_and_business/investment_services")</p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addDomain(newDomain)}
            placeholder="Add a domain..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
          />
          <button
            onClick={() => addDomain(newDomain)}
            className="px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Common Domains */}
        <p className="text-xs text-gray-500 mb-2">Common domains (click to add):</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {COMMON_DOMAINS.filter(d => !formData.domains.includes(d)).slice(0, 6).map(domain => (
            <button
              key={domain}
              onClick={() => addDomain(domain)}
              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs transition"
            >
              + {domain}
            </button>
          ))}
        </div>

        {formData.domains.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.domains.map(domain => (
              <span key={domain} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                {domain}
                <button onClick={() => removeDomain(domain)} className="hover:text-green-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STEP 4: ADVANCED OPTIONS
// =============================================================================

function Step4Advanced({ formData, updateFormData, updateTrustMechanism }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Advanced Options (Optional)</h2>
        <p className="text-gray-600">
          Configure trust mechanisms, active status, and payment support
        </p>
        <p className="text-sm text-gray-500 mt-2">
          These options help users understand your agent's trust model and operational status. All fields are optional and can be updated later.
        </p>
      </div>

      {/* Trust Mechanisms */}
      <div className="border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Supported Trust Mechanisms (optional)</h3>
        <p className="text-xs text-gray-500 mb-4">Select the trust mechanisms your agent supports for validation and reputation</p>

        <div className="space-y-4">
          {/* Reputation-based Trust */}
          <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
            <input
              type="checkbox"
              checked={formData.trustMechanisms.reputation}
              onChange={(e) => updateTrustMechanism('reputation', e.target.checked)}
              className="mt-1 w-5 h-5 text-[#3498db] rounded border-gray-300 focus:ring-[#3498db]"
            />
            <div>
              <div className="font-medium text-gray-900">Reputation-based Trust</div>
              <p className="text-sm text-gray-600">
                Validators provide subjective feedback on agent performance and behavior (thumbs up/down, star ratings, text reviews)
              </p>
            </div>
          </label>

          {/* Crypto-economic Trust */}
          <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
            <input
              type="checkbox"
              checked={formData.trustMechanisms.cryptoEconomic}
              onChange={(e) => updateTrustMechanism('cryptoEconomic', e.target.checked)}
              className="mt-1 w-5 h-5 text-[#3498db] rounded border-gray-300 focus:ring-[#3498db]"
            />
            <div>
              <div className="font-medium text-gray-900">Crypto-economic Trust</div>
              <p className="text-sm text-gray-600">
                Validators stake tokens to vouch for agent behavior; slashed if found malicious
              </p>
            </div>
          </label>

          {/* TEE Attestation Trust */}
          <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
            <input
              type="checkbox"
              checked={formData.trustMechanisms.teeAttestation}
              onChange={(e) => updateTrustMechanism('teeAttestation', e.target.checked)}
              className="mt-1 w-5 h-5 text-[#3498db] rounded border-gray-300 focus:ring-[#3498db]"
            />
            <div>
              <div className="font-medium text-gray-900">TEE Attestation Trust</div>
              <p className="text-sm text-gray-600">
                Trusted Execution Environment provides cryptographic proof of agent code and execution integrity
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Agent Status */}
      <div className="border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Agent Status (optional)</h3>
        <p className="text-xs text-gray-500 mb-4">Mark whether your agent is currently active and accepting requests</p>

        <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => updateFormData('isActive', e.target.checked)}
            className="mt-1 w-5 h-5 text-[#3498db] rounded border-gray-300 focus:ring-[#3498db]"
          />
          <div>
            <div className="font-medium text-gray-900">Agent is Active</div>
            <p className="text-sm text-gray-600">
              Check this if your agent is operational and ready to receive requests. Uncheck for maintenance or testing periods.
            </p>
          </div>
        </label>
      </div>

      {/* Payment Protocol Support */}
      <div className="border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Payment Protocol Support (optional)</h3>
        <p className="text-xs text-gray-500 mb-4">Indicate support for the HTTP 402 Payment Required standard</p>

        <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
          <input
            type="checkbox"
            checked={formData.supportsHttp402}
            onChange={(e) => updateFormData('supportsHttp402', e.target.checked)}
            className="mt-1 w-5 h-5 text-[#3498db] rounded border-gray-300 focus:ring-[#3498db]"
          />
          <div>
            <div className="font-medium text-gray-900">HTTP 402 Payment Support</div>
            <p className="text-sm text-gray-600">
              Check this if your agent implements the HTTP 402 standard for paid services (microtransactions, per-request billing)
            </p>
          </div>
        </label>
      </div>
    </div>
  );
}

// =============================================================================
// STEP 5: STORAGE
// =============================================================================

function Step5Storage({ formData, updateFormData }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Metadata Storage</h2>
        <p className="text-gray-600">
          Choose how to store your agent's metadata
        </p>
      </div>

      <div className="space-y-4">
        {/* Auto (Data URI) */}
        <label
          className={`block p-5 border-2 rounded-xl cursor-pointer transition ${formData.storageMethod === 'auto'
            ? 'border-[#3498db] bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-start gap-4">
            <input
              type="radio"
              name="storageMethod"
              checked={formData.storageMethod === 'auto'}
              onChange={() => updateFormData('storageMethod', 'auto')}
              className="mt-1 w-5 h-5 text-[#3498db]"
            />
            <div>
              <div className="font-semibold text-gray-900 mb-1">Auto (Data URI)</div>
              <p className="text-sm text-gray-600">
                We'll automatically convert your metadata to a base64-encoded data URI and store it directly on-chain. This is the simplest option and requires no external hosting.
              </p>
              <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                Recommended
              </span>
            </div>
          </div>
        </label>

        {/* IPFS URL */}
        <label
          className={`block p-5 border-2 rounded-xl cursor-pointer transition ${formData.storageMethod === 'ipfs'
            ? 'border-[#3498db] bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-start gap-4">
            <input
              type="radio"
              name="storageMethod"
              checked={formData.storageMethod === 'ipfs'}
              onChange={() => updateFormData('storageMethod', 'ipfs')}
              className="mt-1 w-5 h-5 text-[#3498db]"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">IPFS URL</div>
              <p className="text-sm text-gray-600 mb-3">
                Use an existing IPFS URL where you've already uploaded your metadata JSON file.
              </p>
              {formData.storageMethod === 'ipfs' && (
                <input
                  type="url"
                  value={formData.ipfsUrl}
                  onChange={(e) => updateFormData('ipfsUrl', e.target.value)}
                  placeholder="ipfs://Qm... or https://ipfs.io/ipfs/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              )}
            </div>
          </div>
        </label>

        {/* HTTPS URL */}
        <label
          className={`block p-5 border-2 rounded-xl cursor-pointer transition ${formData.storageMethod === 'https'
            ? 'border-[#3498db] bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-start gap-4">
            <input
              type="radio"
              name="storageMethod"
              checked={formData.storageMethod === 'https'}
              onChange={() => updateFormData('storageMethod', 'https')}
              className="mt-1 w-5 h-5 text-[#3498db]"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900 mb-1">HTTPS URL</div>
              <p className="text-sm text-gray-600 mb-3">
                Use a standard HTTPS URL pointing to your metadata JSON file.
              </p>
              {formData.storageMethod === 'https' && (
                <input
                  type="url"
                  value={formData.httpsUrl}
                  onChange={(e) => updateFormData('httpsUrl', e.target.value)}
                  placeholder="https://example.com/metadata.json"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#3498db]"
                />
              )}
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}

// =============================================================================
// STEP 6: REVIEW
// =============================================================================

function Step6Review({
  formData,
  selectedChain,
  generateMetadata,
  walletConnected,
  isSubmitting,
  submitError,
  submitSuccess,
  submitTransaction
}) {
  const chain = CHAINS[selectedChain];
  const metadata = generateMetadata();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Review & Submit</h2>
        <p className="text-gray-600">
          Review your agent details before submitting to the blockchain
        </p>
      </div>

      {/* Chain Info */}
      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chain.color }}></div>
          <div>
            <div className="text-sm font-semibold text-gray-900">Deploying to {chain.name}</div>
            <div className="text-xs text-gray-600">Contract: {chain.contracts.identity}</div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Info */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Basic Info
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-xs text-gray-500">Name:</span>
              <p className="font-medium text-gray-900">{formData.name || '—'}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Description:</span>
              <p className="text-sm text-gray-700 line-clamp-2">{formData.description || '—'}</p>
            </div>
            {formData.imageUrl && (
              <img src={formData.imageUrl} alt="Preview" className="w-16 h-16 rounded-lg mt-2" />
            )}
          </div>
        </div>

        {/* Endpoints */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Endpoints ({formData.endpoints.length})
          </h3>
          {formData.endpoints.length > 0 ? (
            <div className="space-y-1">
              {formData.endpoints.slice(0, 3).map((ep, i) => (
                <div key={i} className="text-xs">
                  <span className="px-1.5 py-0.5 bg-[#3498db] text-white rounded uppercase mr-2">{ep.type}</span>
                  <span className="text-gray-600 font-mono">{(ep.url || ep.ensName || ep.didIdentifier || '').slice(0, 30)}...</span>
                </div>
              ))}
              {formData.endpoints.length > 3 && (
                <p className="text-xs text-gray-500">+{formData.endpoints.length - 3} more</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No endpoints added</p>
          )}
        </div>

        {/* Capabilities */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Capabilities
          </h3>
          <div className="space-y-2">
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {formData.tags.slice(0, 4).map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">#{tag}</span>
                ))}
              </div>
            )}
            {formData.skills.length > 0 && (
              <p className="text-xs text-gray-600">{formData.skills.length} skills</p>
            )}
            {formData.domains.length > 0 && (
              <p className="text-xs text-gray-600">{formData.domains.length} domains</p>
            )}
            {formData.tags.length === 0 && formData.skills.length === 0 && formData.domains.length === 0 && (
              <p className="text-sm text-gray-500">None added</p>
            )}
          </div>
        </div>

        {/* Advanced */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </h3>
          <div className="space-y-1 text-sm">
            <p className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${formData.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              {formData.isActive ? 'Active' : 'Inactive'}
            </p>
            {formData.supportsHttp402 && <p className="text-gray-600">HTTP 402 Supported</p>}
            {Object.values(formData.trustMechanisms).some(v => v) && (
              <p className="text-gray-600">
                Trust: {[
                  formData.trustMechanisms.reputation && 'Reputation',
                  formData.trustMechanisms.cryptoEconomic && 'Crypto-economic',
                  formData.trustMechanisms.teeAttestation && 'TEE'
                ].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Raw Metadata Preview */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Generated Metadata</h3>
        </div>
        <div className="p-4 bg-gray-900 max-h-64 overflow-auto">
          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </div>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="mt-1 text-sm text-red-600">{submitError}</p>
        </div>
      )}

      {/* Success Message */}
      {submitSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{submitSuccess.message}</span>
          </div>
          {submitSuccess.txHash && (
            <a
              href={`${chain.explorer}/tx/${submitSuccess.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm text-green-600 hover:underline"
            >
              View transaction <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {submitSuccess.agentId && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-green-700">
                Agent ID: <span className="font-mono font-bold">#{submitSuccess.agentId}</span>
              </p>
              <Link
                to={`/${submitSuccess.chain === 'baseSepolia' ? 'base-sepolia' :
                  submitSuccess.chain === 'lineaSepolia' ? 'linea-sepolia' :
                    submitSuccess.chain === 'polygonAmoy' ? 'polygon-amoy' :
                      submitSuccess.chain === 'hyperevmTestnet' ? 'hyperevm-testnet' :
                        submitSuccess.chain === 'skaleBaseSepolia' ? 'skale-base-sepolia' :

                          submitSuccess.chain === 'bscTestnet' ? 'bsc-testnet-testnet' :
                            submitSuccess.chain}/agent/${submitSuccess.agentId}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm font-medium"
              >
                View Your Agent <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-4 border-t border-gray-200">
        {!walletConnected ? (
          <div className="text-center py-4">
            <p className="text-gray-600 mb-3">Connect your wallet to submit</p>
            <button className="px-6 py-3 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed">
              Wallet Not Connected
            </button>
          </div>
        ) : (
          <button
            onClick={submitTransaction}
            disabled={isSubmitting || submitSuccess?.agentId}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#3498db] to-[#2980b9] hover:from-[#2980b9] hover:to-[#1f5f8b] text-white rounded-xl transition font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting Transaction...
              </>
            ) : submitSuccess?.agentId ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Agent Created Successfully!
              </>
            ) : (
              <>
                <Cpu className="w-5 h-5" />
                Create Agent on {chain.name}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateAgentPage;